import React, { useEffect, useLayoutEffect, useRef } from 'react';
import { cn } from '@rocco/utils/cn';
import { DirectoryProductGridSkeleton } from './DirectoryProductGridSkeleton';
import {
    getSectionsForPage,
    getLayoutForSection,
} from './hooks/useProductViewMore';

/**
 * Pixel distance from viewport at which skeleton loading is triggered.
 * Used by both the IntersectionObserver rootMargin and the immediate
 * mount check to ensure consistent behavior.
 */
const SKELETON_LOAD_THRESHOLD_PX = 2000;

/**
 * Root margin for IntersectionObserver that triggers skeleton loading.
 * Loads skeletons ~2-3 sections ahead of the viewport,
 * providing 2-4 seconds for network requests at average scroll speed.
 */
const SKELETON_LOAD_THRESHOLD = `${SKELETON_LOAD_THRESHOLD_PX}px 0px`;

interface DirectorySkeletonSectionProps {
    /**
     * The page number this skeleton represents (1-indexed).
     * Page 1: 3 sections (A, B, A)
     * Page 2+: 2 sections (continuing alternation)
     */
    pageNumber: number;
    /**
     * Callback fired when this skeleton section becomes visible in the viewport.
     * Used to trigger lazy-loading of actual products for this page.
     */
    onVisible?: (pageNumber: number) => void;
    /**
     * Whether the skeleton is currently loading (after onVisible was called).
     * Used for aria-busy accessibility attribute.
     */
    isLoading?: boolean;
    /**
     * Optional class name for additional styling.
     */
    className?: string;
    /**
     * Whether to include the featured banner skeleton after section 0.
     * Only relevant for page 1.
     */
    showFeaturedBanner?: boolean;
    /**
     * Ref to attach to the section container for scroll-to functionality.
     */
    sectionRef?: React.RefObject<HTMLDivElement>;
    /**
     * The global section index of this skeleton page's first section.
     * Used together with onHeightMeasured to register skeleton heights
     * into the virtualization model for accurate spacer calculations.
     */
    firstSectionIndex?: number;
    /**
     * Callback fired after mount with measured container height.
     * Reports the total height and section count so the virtualization
     * hook can map one DOM element to multiple virtual section indices.
     */
    onHeightMeasured?: (
        firstSectionIndex: number,
        totalHeight: number,
        sectionCount: number,
    ) => void;
}

/**
 * Skeleton placeholder for a page's worth of grid sections.
 *
 * Renders skeleton loaders matching the DirectoryGridSection layout pattern:
 * - Page 1: 3 sections (A, B, A) with optional featured banner after first section
 * - Page 2+: 2 sections (continuing B, A or A, B alternation)
 *
 * Uses IntersectionObserver to detect when the skeleton enters the viewport,
 * triggering lazy-loading of actual products via the `onVisible` callback.
 *
 * @example
 * ```tsx
 * <DirectorySkeletonSection
 *   pageNumber={1}
 *   onVisible={(page) => loadPageProducts(page)}
 *   showFeaturedBanner={true}
 * />
 * ```
 */
const DirectorySkeletonSectionComponent = ({
    pageNumber,
    onVisible,
    isLoading = false,
    className,
    showFeaturedBanner = false,
    sectionRef,
    firstSectionIndex,
    onHeightMeasured,
}: DirectorySkeletonSectionProps) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const hasTriggeredRef = useRef(false);

    // Get section indices and layouts for this page
    const sections = getSectionsForPage(pageNumber);

    // Reset trigger flag BEFORE the mount check runs on pageNumber change.
    // Must be useLayoutEffect (not useEffect) so it fires before the mount-check
    // useLayoutEffect below — otherwise the mount check would see the stale flag
    // from the previous page and skip the early dispatch.
    useLayoutEffect(() => {
        hasTriggeredRef.current = false;
    }, [pageNumber]);

    // Immediate mount check: if the skeleton is already near the viewport
    // (e.g., deep-linked to a high page, skeleton pages above are within range),
    // trigger loading without waiting for IntersectionObserver.
    // Uses useLayoutEffect to fire before paint, matching the height measurement pattern.
    useLayoutEffect(() => {
        // hasTriggeredRef is set by the IO callback only; this check prevents a
        // redundant mount-check call if this effect re-runs (e.g., onVisible
        // reference change) after IO has already triggered for this pageNumber.
        if (!onVisible || hasTriggeredRef.current || !containerRef.current)
            return;
        const rect = containerRef.current.getBoundingClientRect();
        const isNearViewport =
            rect.bottom >= -SKELETON_LOAD_THRESHOLD_PX &&
            rect.top <= window.innerHeight + SKELETON_LOAD_THRESHOLD_PX;
        if (isNearViewport) {
            onVisible(pageNumber);
        }
    }, [onVisible, pageNumber]);

    // Set up IntersectionObserver for lazy-loading trigger.
    // Always sets up regardless of whether useLayoutEffect already called onVisible,
    // because the early dispatch may silently fail (all fetchers busy, stale state guard, etc.).
    // IO serves as the reliable fallback; dedup is handled by loadSkeletonPageImpl's guards.
    useEffect(() => {
        if (!onVisible) return;

        const observer = new IntersectionObserver(
            (entries) => {
                const [entry] = entries;
                if (entry?.isIntersecting && !hasTriggeredRef.current) {
                    hasTriggeredRef.current = true;
                    onVisible(pageNumber);
                }
            },
            {
                // Start loading well before the skeleton is fully visible
                rootMargin: SKELETON_LOAD_THRESHOLD,
                threshold: 0,
            },
        );

        const element = containerRef.current;
        if (element) {
            observer.observe(element);
        }

        return () => {
            if (element) {
                observer.unobserve(element);
            }
        };
    }, [onVisible, pageNumber]);

    // Report measured height to the virtualization model so spacer
    // calculations use real DOM heights instead of static estimates.
    useLayoutEffect(() => {
        if (onHeightMeasured === undefined || firstSectionIndex === undefined)
            return;
        const el = containerRef.current;
        if (!el) return;
        const height = el.getBoundingClientRect().height;
        if (height > 0) {
            onHeightMeasured(firstSectionIndex, height, sections.length);
        }
    }, [
        onHeightMeasured,
        firstSectionIndex,
        sections.length,
        showFeaturedBanner,
    ]);

    return (
        <div
            ref={(node) => {
                // Combine refs: internal ref for IntersectionObserver + external ref for scroll-to
                (
                    containerRef as React.MutableRefObject<HTMLDivElement | null>
                ).current = node;
                if (sectionRef && 'current' in sectionRef) {
                    (
                        sectionRef as React.MutableRefObject<HTMLDivElement | null>
                    ).current = node;
                }
            }}
            className={cn('relative', className)}
            data-page={pageNumber}
            aria-busy={isLoading}
            aria-live="polite"
            aria-atomic="true"
        >
            {/* Screen reader announcement for loading state */}
            {isLoading && (
                <span className="sr-only">
                    Loading page {pageNumber} products...
                </span>
            )}
            {sections.map((sectionIndex, idx) => (
                <React.Fragment key={`skeleton-section-${sectionIndex}`}>
                    <DirectoryProductGridSkeleton
                        layout={getLayoutForSection(sectionIndex)}
                        className={idx > 0 ? 'pt-20' : undefined}
                    />

                    {/* Featured Banner Skeleton - only after section 0 on page 1 */}
                    {sectionIndex === 0 && showFeaturedBanner && (
                        <FeaturedBannerSkeleton className="mt-20" />
                    )}
                </React.Fragment>
            ))}
        </div>
    );
};

/**
 * Memoized skeleton section to prevent unnecessary re-renders.
 * Uses custom comparison to avoid re-renders from callback/ref changes.
 */
export const DirectorySkeletonSection = React.memo(
    DirectorySkeletonSectionComponent,
    (prevProps, nextProps) => {
        return (
            prevProps.pageNumber === nextProps.pageNumber &&
            prevProps.isLoading === nextProps.isLoading &&
            prevProps.className === nextProps.className &&
            prevProps.showFeaturedBanner === nextProps.showFeaturedBanner &&
            prevProps.firstSectionIndex === nextProps.firstSectionIndex
        );
    },
);

/**
 * Skeleton placeholder for the featured banner.
 * Matches the FeaturedBanner component's dimensions.
 */
const FeaturedBannerSkeleton = ({ className }: { className?: string }) => (
    <div
        className={cn(
            'w-full animate-pulse rounded-lg bg-gray-200',
            // Mobile: ~315px, Desktop: ~630px (matching FeaturedBanner aspect ratio)
            'h-[19.6875rem] md:h-[39.375rem]',
            className,
        )}
        role="status"
        aria-label="Loading featured content"
    >
        <span className="sr-only">Loading featured banner...</span>
    </div>
);

DirectorySkeletonSection.displayName = 'DirectorySkeletonSection';
